import {
  dateFromDateString,
  DateString,
  HolidayEvent,
  Store,
  StoreRtoFlags,
  toDateString,
} from '@nextforce/api'
import { Icon, ListItem, makeStyles, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { Item } from 'holiday_events/items'
import { useCallback, useMemo, useState } from 'react'
import { FlatList, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { DateTimeChooser } from 'shared/components/list/date_time_chooser'
import { StoreChooser } from 'shared/components/store_chooser'

interface EditHolidayEventViewProps {
  stores?: Store[]
  selectedStores?: Store[]
  holidayEvent?: HolidayEvent
  storesRtoFlags: StoreRtoFlags[]
  onChangeStoresRtoFlags: (storesRtoFlags: StoreRtoFlags[]) => void
}

export function EditHolidayEventView({
  stores,
  selectedStores,
  holidayEvent,
  storesRtoFlags,
  onChangeStoresRtoFlags,
}: EditHolidayEventViewProps) {
  const styles = useStyles()
  const { theme } = useTheme()
  const [date, _] = useState<DateString>(toDateString(holidayEvent?.date))

  const items = useMemo(() => {
    const holidayTileItem: Item = {
      type: 'holidayTile',
      key: 'holidayTile',
    }
    const dateItem: Item = {
      type: 'date',
      key: 'date',
    }
    const descriptionItem: Item = {
      type: 'description',
      key: 'description',
    }
    const storeItem: Item = {
      type: 'stores',
      key: 'stores',
    }
    const HeadingItem: Item = {
      type: 'heading',
      key: 'heading',
    }
    return [holidayTileItem, dateItem, HeadingItem, descriptionItem, storeItem]
  }, [])

  const onSelectStores = useCallback(
    (currentSelectedStores: Store[]) => {
      const selectedStoresIds = currentSelectedStores.map(store => store.id)

      const updatedStoresRtoFlags: StoreRtoFlags[] = storesRtoFlags.map(
        flag => ({
          ...flag,
          blockRto: selectedStoresIds.includes(flag.storeId),
        })
      )

      onChangeStoresRtoFlags(updatedStoresRtoFlags)
    },
    [onChangeStoresRtoFlags, storesRtoFlags]
  )

  const renderItem = useCallback(
    ({ item }: { item: Item }) => {
      if (!stores || !holidayEvent) {
        return null
      }
      switch (item?.type) {
        case 'holidayTile':
          return (
            <ListItem containerStyle={{ paddingVertical: 0 }}>
              <ListItem.Content style={styles.holidayTile}>
                <Icon
                  type="font-awesome-5"
                  name="gifts"
                  solid
                  size={20}
                  color={theme.colors?.primary}
                  containerStyle={styles.iconContainerStyle}
                />
                <ListItem.Title style={styles.name}>
                  {holidayEvent.name}
                </ListItem.Title>
              </ListItem.Content>
            </ListItem>
          )
        case 'date':
          return (
            <DateTimeChooser
              title={t('date')}
              value={dateFromDateString(date)}
              mode="date"
              disabled={true}
              onChange={() => null}
            />
          )
        case 'description':
          return (
            <ListItem containerStyle={styles.descriptionContainer}>
              <ListItem.Content>
                <ListItem.Title style={styles.description}>
                  {t('editHolidayEventDescription')}
                </ListItem.Title>
              </ListItem.Content>
            </ListItem>
          )

        case 'stores':
          return (
            <StoreChooser
              stores={stores}
              selectedStores={selectedStores}
              title={t('stores')}
              onChange={onSelectStores}
              required={false}
              value={undefined}
            />
          )

        case 'heading':
          return (
            <ListItem containerStyle={styles.headingContainer}>
              <ListItem.Content>
                <ListItem.Title style={styles.heading}>
                  {t('editHolidayEventHeading')}
                </ListItem.Title>
              </ListItem.Content>
            </ListItem>
          )

        default:
          return null
      }
    },
    [
      date,
      holidayEvent,
      onSelectStores,
      selectedStores,
      stores,
      styles.heading,
      styles.description,
      styles.headingContainer,
      styles.descriptionContainer,
      styles.holidayTile,
      styles.iconContainerStyle,
      styles.name,
      theme.colors?.primary,
    ]
  )

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <FlatList
        style={styles.container}
        data={items}
        renderItem={renderItem}
        ItemSeparatorComponent={() => <View style={styles.itemSeparator} />}
      />
    </SafeAreaView>
  )
}

export const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    backgroundColor: theme.colors?.white,
    flex: 1,
    marginTop: 30,
  },
  holidayTile: {
    flexDirection: 'row',
  },
  itemSeparator: {
    marginVertical: 5,
  },
  headingContainer: {
    marginBottom: 0,
    paddingBottom: 5,
  },
  descriptionContainer: {
    marginTop: 0,
    paddingTop: 5,
  },
  description: {
    textAlign: 'justify',
    fontSize: 15,
    fontFamily: font.normal,
  },
  heading: {
    testAlign: 'justify',
    fontSize: 17,
    fontFamily: font.bold,
  },
  name: {
    flex: 1,
    fontSize: 18,
    fontFamily: font.bold,
    color: theme.colors?.black,
    textAlignVertical: 'center',
    justifyContent: 'center',
  },
  iconContainerStyle: {
    paddingRight: 5,
    marginRight: 5,
  },
}))
